import React, { useEffect, useRef, useState } from 'react';
import AlertConditions from './AlertConditions';
import { AlertSubstitutions } from './AlertQueryPreview';
import Chip from '../../shared/Chip';
import { effectiveMaxMatches } from '../alertSpec';
import './AlertConversationPanel.css';

/**
 * Step 2 as a conversation (PRD-1381).
 *
 * One bordered box: thread above, composer below, a hairline between. **Both the
 * thread and the hairline stay hidden until the first message**, so at rest this
 * reads as a single textarea rather than announcing itself as a chat — an empty
 * thread pane with a cursor blinking in it tells the author they are about to be
 * interviewed, which is not the impression the step wants to open with.
 *
 * Presentational: every decision about what a turn means lives in
 * `useAlertConversation`. This renders what it is given and reports what the
 * author did.
 */

const PLACEHOLDERS = {
  // Tracks state, because the same prompt at three different moments is three
  // different instructions.
  empty: 'Describe the alert in your own words',
  answering: 'Type your answer, or pick one above',
  settled: 'Change anything, or add a condition',
  // No dataset, no conversation: the server refuses the turn with a 422, and the
  // generic "could not be sent, try again" it became never mentioned datasets.
  // Said here, at the moment it matters, rather than as a banner before the
  // author has done anything (review of #221).
  noDataset: 'Select a dataset above to start',
};

const AlertConversationPanel = ({
  messages = [],
  pending = false,
  capped = false,
  settled = false,
  definition = null,
  error = null,
  retryAfter = null,
  staleDefinition = false,
  datasetsChangedMidConversation = false,
  datasetTags = [],
  onRemoveDataset = null,
  onSend = () => {},
  onBuild = null,
  dataFreshness = null,
  volumeGuardMaxMatches = null,
  servedMaxMatches = null,
  onStartAgain = null,
  needsDataset = false,
  onFinishInForm = null,
}) => {
  const [draft, setDraft] = useState('');
  const [answered, setAnswered] = useState({});
  const threadEndRef = useRef(null);
  const lastAgentRef = useRef(null);

  const started = messages.length > 0;
  const lastAgent = [...messages].reverse().find((m) => m.role === 'assistant');
  const options = lastAgent?.options || [];

  // Move focus to the newest question rather than leaving it in the composer.
  // A thread that grows silently is unusable with a screen reader: the author
  // has no way to know a question arrived.
  useEffect(() => {
    if (!pending && lastAgent && lastAgentRef.current) {
      lastAgentRef.current.focus?.();
    }
    threadEndRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages.length, pending, lastAgent]);

  const submitDraft = (event) => {
    event?.preventDefault?.();
    const text = draft.trim();
    if (!text || pending || needsDataset) return;
    setDraft('');
    onSend(text);
  };

  const pickOption = (option) => {
    if (pending) return;
    // The picked one stays lit and the rest retire — the author can see what
    // they chose without scrolling back, and cannot re-answer a settled question
    // by clicking a chip that should no longer be live.
    setAnswered((prev) => ({ ...prev, [messages.length]: option }));
    onSend(option);
  };

  const placeholder = needsDataset
    ? PLACEHOLDERS.noDataset
    : !started
      ? PLACEHOLDERS.empty
      : settled
        ? PLACEHOLDERS.settled
        : PLACEHOLDERS.answering;

  return (
    <div className="alert-conversation">
      <div className="alert-conversation-box">
        {/* Hidden until the first message, hairline and all. */}
        {started && (
          <div className="alert-conversation-thread" role="log" aria-live="polite" aria-label="Alert conversation">
            {messages.map((message, index) => {
              const isAgent = message.role === 'assistant';
              const picked = answered[index + 1];
              return (
                <div
                  key={`${message.role}-${index}`}
                  className={`alert-conversation-turn alert-conversation-turn-${isAgent ? 'agent' : 'author'}`}
                >
                  <p className="alert-conversation-text">{message.content}</p>
                  {isAgent && Array.isArray(message.options) && message.options.length > 0 && (
                    <div className="alert-conversation-chips">
                      {message.options.map((option) => {
                        const isPicked = picked === option;
                        const retired = Boolean(picked);
                        return (
                          <button
                            key={option}
                            type="button"
                            className={
                              `alert-conversation-chip${isPicked ? ' alert-conversation-chip-picked' : ''}`
                              + `${retired && !isPicked ? ' alert-conversation-chip-retired' : ''}`
                            }
                            onClick={() => pickOption(option)}
                            disabled={retired || pending}
                            aria-pressed={isPicked}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {pending && (
              <p className="alert-conversation-pending" role="status">
                {/* Honest rather than decorative: a Build is a 29s median and an
                    86s maximum, so "thinking…" for a minute reads as broken. */}
                Working on it. Building an alert takes up to a minute.
              </p>
            )}
            <div ref={threadEndRef} />
          </div>
        )}

        {started && <div className="alert-conversation-hairline" />}

        <form className="alert-conversation-composer" onSubmit={submitDraft}>
          <textarea
            className="alert-conversation-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={started ? 2 : 3}
            disabled={pending || capped || needsDataset}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter breaks the line. A multi-line
              // description is normal here, so Enter-always-sends would truncate
              // people mid-thought.
              if (e.key === 'Enter' && !e.shiftKey) submitDraft(e);
            }}
          />
          {/*
            The datasets and a send button, in the box like Ask ResAI's. Enter
            already sent, but nothing on screen said so: a composer with no
            button reads as a notes field, and the author waits for a reply to a
            message they never sent.
          */}
          <div className="alert-conversation-toolbar">
            {datasetTags.length > 0 && (
              <div className="alert-conversation-using">
                <span className="alert-conversation-using-label">Using:</span>
                {datasetTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    className="alert-conversation-tag"
                    onDismiss={onRemoveDataset ? () => onRemoveDataset(tag.id) : undefined}
                    dismissLabel={`Remove ${tag.name}`}
                  >
                    {tag.name}
                  </Chip>
                ))}
              </div>
            )}
            <button
              type="submit"
              className="alert-conversation-send"
              aria-label="Send"
              disabled={pending || capped || needsDataset || !draft.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </div>
          {/*
            The turn cap. The server will not advance this conversation again, so
            a live composer would take what the author types and do nothing with
            it — the silent stop the cap exists to avoid. What they have built so
            far is kept; only the conversation is over.
          */}
          {/*
            Two ways out, and both are real now (PRD-1447). This used to read
            "Build what you have, or start again" with neither on the screen: a
            capped turn carries no built alert to build from, and nothing called
            the hook's reset. The only way out was closing the wizard.
          */}
          {capped && (
            <div className="alert-conversation-capped">
              <p className="alert-conversation-or" role="status">
                This conversation has reached its limit. Finish it in the form with what you have
                so far, or start again.
              </p>
              <div className="alert-conversation-capped-actions">
                {onFinishInForm && (
                  <button type="button" className="reports-wizard-btn reports-wizard-btn-primary" onClick={onFinishInForm}>
                    Finish in the form
                  </button>
                )}
                {onStartAgain && (
                  <button type="button" className="resui-btn resui-btn--secondary" onClick={onStartAgain}>
                    Start again
                  </button>
                )}
              </div>
            </div>
          )}
          {!capped && options.length > 0 && (
            <p className="alert-conversation-or">or answer in your own words above</p>
          )}
        </form>
      </div>

      {datasetsChangedMidConversation && (
        <p className="alert-conversation-notice" role="status">
          You changed the data this alert reads. Answers you have already given may refer to
          something that is no longer selected.
        </p>
      )}

      {staleDefinition && (
        <p className="alert-conversation-notice alert-conversation-notice-warn" role="alert">
          You changed the data this alert reads, so it needs building again before you continue.
        </p>
      )}

      {error && (
        <p className="alert-conversation-notice alert-conversation-notice-warn" role="alert">
          {retryAfter
            ? `Too many alerts are being built right now. Try again in about ${retryAfter} seconds.`
            : error.message}
        </p>
      )}

      {onBuild && (
        <button
          type="button"
          className="alert-conversation-build"
          onClick={onBuild}
          // Disabled until the agent settles: building half a conversation
          // produces an alert nobody described.
          disabled={!settled || pending}
        >
          Build this alert
        </button>
      )}

      {definition && (
        <AlertConversationPreview
          definition={definition}
          dataFreshness={dataFreshness}
          volumeGuardMaxMatches={volumeGuardMaxMatches}
          servedMaxMatches={servedMaxMatches}
        />
      )}
    </div>
  );
};

/**
 * The preview. Rows, with a denominator.
 *
 * **The denominator is not decoration.** "3 of 142" is an alert; "214 of 142" is
 * a misconfiguration, and a bare matched count cannot tell them apart — which
 * matters most here, because the number of matches is the number of messages
 * step 3 will send.
 */
export const AlertConversationPreview = ({
  definition,
  dataFreshness = null,
  volumeGuardMaxMatches = null,
  servedMaxMatches = null,
}) => {
  const rows = definition?.rows || [];
  const columns = definition?.columns || [];
  // The conversation's `definition` is relayed raw, so the uncapped total arrives
  // as `match_count`; reading only `matchCount` fell back to the capped row count.
  // Never fewer than the rows shown, as in `AlertQueryPreview`: a count that
  // failed and defaulted to 0 must not render "nothing matched" over a full table.
  const matched = Math.max(definition?.match_count ?? definition?.matchCount ?? 0, rows.length);
  const population = definition?.populationCount ?? null;

  return (
    <div className="alert-conversation-preview">
      <AlertSubstitutions substitutions={definition?.substitutions} />
      {rows.length > 0 && (
        <table className="alert-conversation-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.name} scope="col">{column.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.name}>{String(row[column.name] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        Rendered from the stored IR, beneath the rows it produced. The author
        verifies output above and can check what was filtered here — without
        it, a model that misread the request produces a plausible preview that
        gets approved.
      */}
      <AlertConditions sql={definition?.sql} window={definition?.window} />

      {matched > 0 ? (
        <>
          <p className="alert-conversation-summary">
            {population != null
              ? `${matched} of ${population} match right now.`
              : `${matched} match right now.`}
            {' '}
            Nothing sends until you finish the next two steps.
          </p>
          <AlertFanoutConsequence
            matched={matched}
            volumeGuardMaxMatches={volumeGuardMaxMatches}
            servedMaxMatches={servedMaxMatches}
          />
        </>
      ) : (
        <ZeroMatchExplanation population={population} dataFreshness={dataFreshness} />
      )}
    </div>
  );
};

/**
 * What this many matches actually costs, stated where the threshold is chosen.
 *
 * **The consequence belongs at Build, not in step 3.** Step 3 already warns that
 * recipients receive every subject the alert matches — but by then the threshold
 * that decides whether that is 3 messages or 214 has been set and left behind.
 * The author is told the price at the screen where they can still change it.
 *
 * The arithmetic is the far side's, not a guess: `fanout.py` plans one message
 * per matched subject per recipient — *"an alert that matched three members and
 * carries two recipients owes six messages"*. Recipients are chosen in step 3 and
 * are unknown here, so this states the per-recipient figure rather than inventing
 * a total from a list that does not exist yet.
 *
 * Above the cap it says something sharper, because the outcome differs in kind:
 * `volume_guard` **holds the whole run** rather than trimming it, so an author who
 * sails past the cap gets nothing delivered and a review task. Better to learn
 * that while setting the threshold than from an alert that never arrived.
 */
export const AlertFanoutConsequence = ({
  matched,
  volumeGuardMaxMatches = null,
  servedMaxMatches = null,
}) => {
  // **Prefer the cap the server just applied over the one computed here.**
  //
  // The default lives in two repos — `VOLUME_GUARD_DEFAULT_MAX_MATCHES` here and
  // `volume_guard.DEFAULT_MAX_MATCHES` there — and nothing structural holds them
  // together, so if the Python constant moves this screen keeps previewing the
  // old number while the live run applies the new one. That is the
  // preview-versus-live divergence the Python docstring says the default exists
  // to prevent, reappearing one layer up.
  //
  // The dry run already reports the effective cap (`volumeGuard.threshold`,
  // fed from `effective_max_matches`), so once there is a result the local
  // constant is not consulted at all. It survives only as the placeholder before
  // the first preview, where nothing better exists.
  const cap = servedMaxMatches ?? effectiveMaxMatches(volumeGuardMaxMatches);
  // Strictly greater, matching `volume_guard.evaluate_guard`: matched === cap is
  // within the cap, and saying otherwise would warn about a run that does send.
  const overCap = matched > cap;
  const unit = matched === 1 ? 'message' : 'messages';

  return (
    <p
      className={overCap
        ? 'alert-conversation-fanout alert-conversation-fanout-over'
        : 'alert-conversation-fanout'}
      {...(overCap ? { role: 'status' } : {})}
    >
      Each match becomes its own message, to every recipient you add in step 3
      {' '}&mdash; {matched} {unit} per recipient.
      {overCap && (cap === 0 ? (
        <>
          {' '}The volume guard is set to hold every run, so this would be held for review
          rather than sent.
        </>
      ) : (
        <>
          {' '}That is above the volume guard&rsquo;s cap of {cap}, so a run this size is held
          for review instead of sent &mdash; all of it, not the first {cap}.
        </>
      ))}
    </p>
  );
};

/**
 * Nothing matched — which means one of three different things.
 *
 * A good alert that has not fired, logic that is wrong, or data that stopped
 * arriving. The author cannot tell them apart from "0", and on dev the third is
 * routinely the answer: a correct 30-day alert previews empty because the newest
 * row is a fortnight old.
 *
 * So this says which one it can rule out. When freshness is known it is stated
 * outright; when it is not, the ambiguity is named rather than left implied —
 * that is honest, and it is still more than "0 matched" says.
 */
export const ZeroMatchExplanation = ({ population = null, dataFreshness = null }) => {
  const stale = dataFreshness?.newestRowAgeDays != null
    && dataFreshness?.windowDays != null
    && dataFreshness.newestRowAgeDays > dataFreshness.windowDays;

  return (
    <div className="alert-conversation-summary alert-conversation-summary-empty" role="status">
      <p>
        {population != null ? `Nothing matches right now, out of ${population}.` : 'Nothing matches right now.'}
      </p>
      {stale ? (
        <p>
          The newest row in this data is {dataFreshness.newestRowAgeDays} days old, and this alert
          looks at the last {dataFreshness.windowDays} days — so there is nothing recent for it to
          match. The alert may still be right; the data has stopped arriving.
        </p>
      ) : dataFreshness?.newestRowAgeDays != null ? (
        <p>
          The data is current — the newest row is {dataFreshness.newestRowAgeDays} days old — so
          this is an alert that simply has not fired, rather than one looking at stale data.
        </p>
      ) : (
        <p>
          That can mean the alert is correct and has not fired, or that it is looking for something
          the data does not contain. Check the conditions below before continuing.
        </p>
      )}
    </div>
  );
};

export default AlertConversationPanel;
